import { useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Progress,
  WarningPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SyncIcon from '@material-ui/icons/Sync';

type UserRow = {
  user_login: string;
  team_count: number;
  repo_permission_count: number;
};

type UserRepo = {
  repo: string;
  visibility: 'public' | 'private';
  permission: 'read' | 'write' | 'admin';
  source: 'team' | 'direct';
  source_team_slug?: string;
};

export const UsersPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userRepos, setUserRepos] = useState<UserRepo[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState<string | undefined>();
  const [reposVisibility, setReposVisibility] = useState<
    'all' | 'public' | 'private'
  >('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteRole, setInviteRole] = useState<'direct_member' | 'admin'>(
    'direct_member',
  );
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const loadUsers = async (
    usernameToInclude?: string | null,
    filterToUserOnly?: boolean,
  ) => {
    try {
      setError(undefined);
      setLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(`${adminBaseUrl}/github/users`);
      if (!res.ok) {
        throw new Error(`Failed to load users: ${res.statusText}`);
      }
      const data = await res.json();
      let loadedUsers = data.users || [];

      // Ensure current user is in the list (use parameter or state)
      const userToCheck = usernameToInclude || currentUser;
      if (
        userToCheck &&
        !loadedUsers.find((u: UserRow) => u.user_login === userToCheck)
      ) {
        loadedUsers.push({
          user_login: userToCheck,
          team_count: 0,
          repo_permission_count: 0,
        });
      }

      if (filterToUserOnly && userToCheck) {
        loadedUsers = loadedUsers.filter(
          (u: UserRow) => u.user_login === userToCheck,
        );
      }

      setUsers(loadedUsers);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadUserRepos = async (
    username: string,
    visibility: 'all' | 'public' | 'private' = 'all',
  ) => {
    try {
      setReposError(undefined);
      setReposLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/users/${username}/repos?visibility=${visibility}`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load repos: ${res.statusText}`);
      }
      const data = await res.json();
      setUserRepos(data.repos || []);
    } catch (e: unknown) {
      setReposError(e instanceof Error ? e.message : String(e));
    } finally {
      setReposLoading(false);
    }
  };

  const handleUserClick = async (user: UserRow) => {
    setSelectedUser(user.user_login);
    await loadUserRepos(user.user_login, reposVisibility);
  };

  const handleCloseUserDialog = () => {
    setSelectedUser(null);
    setUserRepos([]);
    setReposError(undefined);
  };

  const handleVisibilityChange = async (
    newVisibility: 'all' | 'public' | 'private',
  ) => {
    setReposVisibility(newVisibility);
    if (selectedUser) {
      await loadUserRepos(selectedUser, newVisibility);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteUsername.trim()) {
      setError('Username is required');
      return;
    }
    try {
      setInviting(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(`${adminBaseUrl}/github/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: inviteUsername.trim(),
          role: inviteRole,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to invite user: ${res.statusText}`,
        );
      }
      setInviteDialogOpen(false);
      setInviteUsername('');
      setInviteRole('direct_member');
      // Wait a bit for the invitation to be processed
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUsers(currentUser);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveUserClick = (username: string) => {
    setUserToRemove(username);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;
    try {
      setRemoving(userToRemove);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/users/${userToRemove}`,
        {
          method: 'DELETE',
        },
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to remove user: ${res.statusText}`,
        );
      }
      setConfirmRemoveOpen(false);
      setUserToRemove(null);
      setSuccessMessage(`Successfully removed ${userToRemove} from the organization`);
      setTimeout(() => setSuccessMessage(undefined), 4000);
      await loadUsers(currentUser);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRemoving(null);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(`${adminBaseUrl}/sync`, {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error(`Sync failed: ${res.statusText}`);
      }
      // Wait a bit for sync to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Reload users after sync (pass currentUser to ensure it's included)
      if (isAdmin) {
        await loadUsers(currentUser);
      } else if (currentUser) {
        // For non-admin, reload their repos
        await loadUserRepos(currentUser, reposVisibility);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        // Get current user
        const identity = await identityApi.getBackstageIdentity();
        const userRef = identity.userEntityRef;
        const username = userRef.split('/').pop() || null;
        setCurrentUser(username);

        // Check if user is admin
        // Admin groups are configured in permission.adminGroups
        const adminGroups =
          configApi.getOptionalStringArray('permission.adminGroups') || [];

        const allEntityRefs = identity.ownershipEntityRefs || [];
        const userGroups = allEntityRefs
          .filter(ref => ref.startsWith('group:')) // Only group refs (format: group:default/admin)
          .map(ref => {
            const parts = ref.split('/');
            return parts[parts.length - 1]; // Extract group name/slug
          });

        const userIsAdmin =
          adminGroups.length > 0 &&
          adminGroups.some(adminGroup => userGroups.includes(adminGroup));
        setIsAdmin(userIsAdmin);

        if (userIsAdmin) {
          await loadUsers(username, false);
        } else if (username) {
          await loadUsers(username, true);
          await loadUserRepos(username, reposVisibility);
          setSelectedUser(username);
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userColumns: TableColumn<UserRow>[] = [
    { title: 'User', field: 'user_login' },
    { title: 'Teams', field: 'team_count', type: 'numeric' },
    { title: 'Repo perms', field: 'repo_permission_count', type: 'numeric' },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            render: (rowData: UserRow) => (
              <Tooltip title="Remove from organization">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveUserClick(rowData.user_login);
                  }}
                  disabled={removing === rowData.user_login}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const repoColumns: TableColumn<UserRepo>[] = [
    { title: 'Repository', field: 'repo' },
    { title: 'Visibility', field: 'visibility' },
    { title: 'Permission', field: 'permission' },
    { title: 'Source', field: 'source' },
    { title: 'Team', field: 'source_team_slug' },
  ];

  let body;
  if (loading) {
    body = <Progress />;
  } else {
    body = (
      <>
        {error && <WarningPanel title="Error" message={error} />}
        {successMessage && (
          <div
            style={{
              background: '#e8f5e9',
              color: '#1b5e20',
              padding: '12px 16px',
              borderRadius: 4,
              marginBottom: 16,
            }}
          >
            {successMessage}
          </div>
        )}
        {isAdmin ? (
          <InfoCard
            title="Users"
            action={
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {syncing ? 'Syncing...' : 'Sync GitHub'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteDialogOpen(true)}
                >
                  Invite User
                </Button>
              </div>
            }
          >
            <Table
              title="Users"
              options={{ paging: true, pageSize: 10 }}
              columns={userColumns}
              data={users}
              onRowClick={(_, rowData) => handleUserClick(rowData as UserRow)}
            />
          </InfoCard>
        ) : (
          <InfoCard
            title={`My Account${currentUser ? ` (${currentUser})` : ''}`}
          >
            <Table
              title="My Information"
              options={{ paging: false, search: false, toolbar: false }}
              columns={userColumns.filter(col => col.field !== 'Actions')}
              data={users}
              onRowClick={(_, rowData) => handleUserClick(rowData as UserRow)}
            />
            {selectedUser && (
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 150 }}
                  >
                    <InputLabel>Visibility</InputLabel>
                    <Select
                      value={reposVisibility}
                      onChange={e =>
                        handleVisibilityChange(
                          e.target.value as 'all' | 'public' | 'private',
                        )
                      }
                      label="Visibility"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                {reposLoading && <Progress />}
                {!reposLoading && reposError && (
                  <WarningPanel title="Error" message={reposError} />
                )}
                {!reposLoading && !reposError && (
                  <Table
                    title="My Repositories"
                    options={{ paging: true, pageSize: 10 }}
                    columns={repoColumns}
                    data={userRepos}
                  />
                )}
              </div>
            )}
          </InfoCard>
        )}
      </>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title={isAdmin ? 'Users Management' : 'My Repositories'}
        subtitle={
          isAdmin
            ? 'Manage organization users'
            : 'View your accessible repositories'
        }
      />
      <Content>{body}</Content>
      {isAdmin && (
        <Dialog
          open={selectedUser !== null}
          onClose={handleCloseUserDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Repositories for {selectedUser}</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: 16 }}>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 150 }}
              >
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={reposVisibility}
                  onChange={e =>
                    handleVisibilityChange(
                      e.target.value as 'all' | 'public' | 'private',
                    )
                  }
                  label="Visibility"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </div>
            {reposLoading && <Progress />}
            {!reposLoading && reposError && (
              <WarningPanel title="Error" message={reposError} />
            )}
            {!reposLoading && !reposError && (
              <Table
                title="Repositories"
                options={{ paging: true, pageSize: 10 }}
                columns={repoColumns}
                data={userRepos}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUserDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog
          open={confirmRemoveOpen}
          onClose={() => setConfirmRemoveOpen(false)}
        >
          <DialogTitle>Confirm Removal</DialogTitle>
          <DialogContent>
            Are you sure you want to remove {userToRemove} from the
            organization?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmRemove}
              color="secondary"
              variant="contained"
              disabled={removing !== null}
            >
              {removing ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invite User to Organization</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="GitHub Username"
              value={inviteUsername}
              onChange={e => setInviteUsername(e.target.value)}
              margin="normal"
              placeholder="Enter GitHub username"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                onChange={e =>
                  setInviteRole(e.target.value as 'direct_member' | 'admin')
                }
                label="Role"
              >
                <MenuItem value="direct_member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleInviteUser}
              color="primary"
              variant="contained"
              disabled={inviting || !inviteUsername.trim()}
            >
              {inviting ? 'Inviting...' : 'Invite'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Page>
  );
};

export default UsersPage;
